import React, { FunctionComponent, ReactNode } from 'react'
import amplitude from 'amplitude-js'
import { Amplitude, AmplitudeProvider as Provider, LogOnMount, useAmplitude } from 'react-amplitude-hooks'
import type { Callback } from 'react-amplitude-hooks/dist/components/Amplitude'
import { useRoutePageName } from '../../config/router'
import { app } from '../../env'
import { useConsumerSelector } from '../../state'
import { useEnduringEventProperties, useUserProperties } from './hooks'
import { AmplitudeEventTypesProperties, PageNames } from './types'
import { maskEmail, maskPhoneNumber } from '@afterpay/utils'

export const viewedPageEventName = (pageName: PageNames): string => `Viewed ${pageName} Page`

export interface TrackingEventProperty {
  [key: string]: unknown
}
export interface TrackingProps {
  eventName: string
  eventProps?: TrackingEventProperty
}

export interface AmplitudeUserProps {
  children: ReactNode
}

export interface AmplitudeProviderProps {
  children: ReactNode
}

export const AmplitudeUser: FunctionComponent<AmplitudeUserProps> = ({ children }) => {
  const userProperties = useUserProperties()
  return <Amplitude userProperties={userProperties}>{children}</Amplitude>
}

export const AmplitudeProvider: FunctionComponent<AmplitudeProviderProps> = ({ children }) => {
  const { uuid } = useConsumerSelector()
  const { AMPLITUDE_CLIENT_SIDE_ID } = app
  const hasValidApiKey = typeof AMPLITUDE_CLIENT_SIDE_ID !== 'undefined' && AMPLITUDE_CLIENT_SIDE_ID.length > 0
  const amplitudeInstance = amplitude.getInstance()

  if (hasValidApiKey) {
    return (
      <Provider amplitudeInstance={amplitudeInstance} apiKey={AMPLITUDE_CLIENT_SIDE_ID} userId={uuid}>
        <AmplitudeUser>{children}</AmplitudeUser>
      </Provider>
    )
  } else {
    return <>{children}</>
  }
}

export const TrackPageViewed: FunctionComponent<{
  eventProps?: TrackingEventProperty
}> = ({ eventProps }) => {
  const pageName = useRoutePageName()
  const eventProperties = useEnduringEventProperties()
  return <LogOnMount eventType={viewedPageEventName(pageName)} eventProperties={{ ...eventProperties, ...eventProps, pageName }} />
}

export const TrackOnMount: FunctionComponent<TrackingProps> = ({ eventName, eventProps }) => {
  const eventProperties = useEnduringEventProperties()
  return <LogOnMount eventType={eventName} eventProperties={{ ...eventProperties, ...eventProps }} />
}

interface HasPortalLogEvent {
  logEvent: <AmplitudeEventType extends keyof AmplitudeEventTypesProperties>(
    eventType: AmplitudeEventType,
    eventPropertiesIn?: AmplitudeEventTypesProperties[AmplitudeEventType],
    callback?: Callback
  ) => void
}

const BLACKLISTED_KEYS: Array<{ name: string; maskMethod: (value: string) => string }> = [
  { name: 'email', maskMethod: maskEmail },
  { name: 'phone', maskMethod: maskPhoneNumber },
]

export function maskBlacklistedKeys<T extends Record<string, unknown>>(obj: T): T {
  if (obj == null || typeof obj !== 'object') {
    return obj
  }
  const masked = { ...obj }
  for (const { name, maskMethod } of BLACKLISTED_KEYS) {
    if (Object.prototype.hasOwnProperty.call(masked, name) && masked[name] != null) {
      ;(masked as Record<string, unknown>)[name] = maskMethod(masked[name] as string)
    }
  }
  return masked
}

type UseAmplitudeWithEnduringEventProperties = Omit<ReturnType<typeof useAmplitude>, 'logEvent'> & HasPortalLogEvent

export function useAmplitudeWithEnduringEventProperties(eventProps: TrackingEventProperty = {}): UseAmplitudeWithEnduringEventProperties {
  const amplitude = useAmplitude({ ...useEnduringEventProperties(), ...eventProps })
  const logEvent: HasPortalLogEvent['logEvent'] = (eventType, eventPropertiesIn, callback) => {
    const maskedProps = maskBlacklistedKeys(eventPropertiesIn ?? {})
    amplitude.logEvent(eventType, maskedProps, callback)
  }
  return {
    ...amplitude,
    logEvent,
  }
}
